/*
 * TABLE OF CONTENTS
 * 1. Scroll Animations
 * 2. Counters
 * 3. Navigation (Navbar, Back-to-Top, Compass)
 * 4. SVG Flow Animation
 * 5. Stagger Grid
 * 6. FAQ Accordion
 * 7. Testimonials Carousel
 * 8. Hero Animation
 * 9. Offcanvas Menu Animation
 */

document.addEventListener('DOMContentLoaded', () => {
  initScrollAnimations();
  initCounters();
  initNavigation();
  initSvgFlow();
  initStaggerGrid();
  initFaq();
  initTestimonials();
  initHeroAnimation();
  initOffcanvasAnimation();
});

/**
 * 1. Scroll Animations
 * Handles visibility classes on scroll using IntersectionObserver.
 */
function initScrollAnimations() {
  const elements = document.querySelectorAll('.dlaz-animate-on-scroll');
  if (!elements.length) return;

  if (!('IntersectionObserver' in window)) {
    elements.forEach(el => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        obs.unobserve(entry.target);
      }
    });
  }, {
    root: null,
    rootMargin: '0px',
    threshold: 0.2
  });

  elements.forEach(el => observer.observe(el));
}

/**
 * 2. Counters
 * Animates numbers counting up when scrolled into view.
 */
function initCounters() {
  const counters = document.querySelectorAll('.dlaz-counter');
  if (!counters.length) return;

  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const counter = entry.target;
        const target = +counter.getAttribute('data-target');
        const duration = 2000;
        const increment = target / (duration / 16); // ~60fps

        let current = 0;
        const updateCounter = () => {
          current += increment;
          if (current < target) {
            counter.innerText = Math.ceil(current);
            requestAnimationFrame(updateCounter);
          } else {
            counter.innerText = target;
            counter.classList.add('is-complete');
          }
        };

        updateCounter();
        obs.unobserve(counter);
      }
    });
  }, { threshold: 0.5 });

  counters.forEach(el => observer.observe(el));
}

/**
 * 3. Navigation
 * Handles Navbar shrinking, Back-to-Top button, and Compass Scrollspy.
 */
function initNavigation() {
  // Shrinking Navbar
  const navbar = document.getElementById('main-nav');
  if (navbar) {
    window.addEventListener('scroll', () => {
      navbar.classList.toggle('is-scrolled', window.scrollY > 50);
    }, { passive: true });
  }

  // Back to Top
  const backToTop = document.getElementById('back-to-top');
  if (backToTop) {
    window.addEventListener('scroll', () => {
      backToTop.classList.toggle('is-visible', window.scrollY > 300);
    }, { passive: true });

    backToTop.addEventListener('click', (e) => {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  // Compass Scrollspy
  const navLinks = document.querySelectorAll('.compass-link');
  const sections = document.querySelectorAll('section');
  
  if (navLinks.length > 0 && sections.length > 0) {
    window.addEventListener('scroll', () => {
      let current = '';
      sections.forEach(section => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.clientHeight;
        // Trigger when section is 1/3rd up the screen
        if (window.scrollY >= (sectionTop - sectionHeight / 3)) {
          current = section.getAttribute('id');
        }
      });

      navLinks.forEach(link => {
        link.classList.toggle('active', link.getAttribute('data-target') === current);
      });
    }, { passive: true });
  }
}

/**
 * 4. SVG Flow Animation
 * Animates SVG paths and nodes in sequence.
 */
function initSvgFlow() {
  const target = document.getElementById('dlaz-flow-svg-container');
  if (!target) return;

  const steps = [
    { arrow: 'step-1-arrow', label: 'step-1-label', node: 'node-zahnarzt' },
    { arrow: 'step-2-arrow', label: 'step-2-label', node: 'node-dlaz' },
    { arrow: 'step-3-arrow', label: 'step-3-label', node: 'node-labor' },
    { arrow: 'step-4-arrow', label: 'step-4-label', node: 'node-dlaz' }
  ];

  const nodeIds = ['node-labor', 'node-dlaz', 'node-zahnarzt'];
  const stepDuration = 2000;
  const pauseBetweenSteps = 0;
  const dotMap = {};

  const showNodes = () => {
    nodeIds.forEach(id => {
      const n = document.getElementById(id);
      n?.classList.add('visible');
    });
  };

  const resetArrows = () => {
    document.querySelectorAll('.step-arrow').forEach(arrow => {
      const len = arrow.getTotalLength();
      Object.assign(arrow.style, {
        transition: 'none',
        strokeDasharray: len,
        strokeDashoffset: len,
        fillOpacity: 0
      });
    });

    Object.values(dotMap).forEach(({ dot }) => {
      dot.style.opacity = 0;
    });

    document.querySelectorAll('.step-label').forEach(l => l.classList.remove('active'));
  };

  const createArrowDots = () => {
    const svgNS = 'http://www.w3.org/2000/svg';
    document.querySelectorAll('.step-arrow').forEach(arrow => {
      const startPoint = arrow.getPointAtLength(0);
      const dot = document.createElementNS(svgNS, 'circle');
      dot.setAttribute('r', 6);
      dot.setAttribute('cx', startPoint.x);
      dot.setAttribute('cy', startPoint.y);
      dot.setAttribute('class', 'arrow-dot');
      arrow.parentNode.appendChild(dot);
      dotMap[arrow.id] = { path: arrow, dot };
    });
  };

  const clearAllActive = () => {
    resetArrows();
    nodeIds.forEach(id => {
      document.getElementById(id)?.classList.remove('pulse');
    });
  };

  const animateArrowForward = (arrow) => {
    if (!arrow) return;
    const len = arrow.getTotalLength();

    // Reset
    Object.assign(arrow.style, {
      transition: 'none',
      strokeDasharray: len,
      strokeDashoffset: len,
      fillOpacity: 0
    });
    void arrow.getBoundingClientRect(); // Force reflow

    // Animate
    arrow.style.transition = 'stroke-dashoffset 1s cubic-bezier(0.20, 1.0, 0.15, 1.0), fill-opacity 0.3s ease-out 1s';
    arrow.style.strokeDashoffset = 0;
    arrow.style.fillOpacity = 1;

    // Dot Animation
    const entry = dotMap[arrow.id];
    if (!entry) return;
    const { dot } = entry;
    const travelDuration = 1200;

    Object.assign(dot.style, {
      opacity: 1,
      transform: 'scale(1)',
      transition: 'opacity 0.12s ease-out'
    });

    const startPoint = arrow.getPointAtLength(0);
    dot.setAttribute('cx', startPoint.x);
    dot.setAttribute('cy', startPoint.y);

    const moveStart = performance.now();
    const moveFrame = (now) => {
      const elapsed = now - moveStart;
      const t = Math.min(elapsed / travelDuration, 1);
      const point = arrow.getPointAtLength(len * t);
      dot.setAttribute('cx', point.x);
      dot.setAttribute('cy', point.y);
      
      if (t < 1) {
        requestAnimationFrame(moveFrame);
      } else {
        dot.style.opacity = 0;
      }
    };
    requestAnimationFrame(moveFrame);
  };

  const runSequence = () => {
    clearAllActive();
    let i = 0;

    const activateStep = () => {
      const step = steps[i];
      const arrow = document.getElementById(step.arrow);
      const label = document.getElementById(step.label);
      const node = document.getElementById(step.node);

      if (label) setTimeout(() => label.classList.add('active'), 200);
      animateArrowForward(arrow);
      if (node) {
        node.classList.add('pulse');
        setTimeout(() => node.classList.remove('pulse'), 400);
      }

      i++;
      if (i < steps.length) {
        setTimeout(activateStep, stepDuration + pauseBetweenSteps);
      }
    };
    activateStep();
  };

  // Initialization
  createArrowDots();
  resetArrows();

  let hasPlayed = false;
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasPlayed) {
          hasPlayed = true;
          showNodes();
          setTimeout(runSequence, 1000);
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    observer.observe(target);
  } else {
    showNodes();
    setTimeout(runSequence, 1000);
  }
}

/**
 * 5. Stagger Grid
 * Uses Anime.js to stagger reveal grid items.
 */
function initStaggerGrid() {
  const staggerGrids = document.querySelectorAll('.dlaz-stagger-grid');
  if (!staggerGrids.length || typeof anime === 'undefined') return;

  staggerGrids.forEach(grid => {
    const items = grid.querySelectorAll('.dlaz-stagger-item');
    if (!items.length) return;

    anime.set(items, { translateY: 50, opacity: 0 });

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          anime({
            targets: items,
            translateY: 0,
            opacity: 1,
            easing: 'easeOutExpo',
            duration: 1000,
            delay: anime.stagger(150)
          });
          obs.unobserve(grid);
        }
      });
    }, {
      rootMargin: '0px 0px -20% 0px',
      threshold: 0.1
    });

    observer.observe(grid);
  });
}

/**
 * 6. FAQ Accordion
 * Handles expansion/collapse of FAQ items.
 */
function initFaq() {
  const headers = document.querySelectorAll('.faq-header');
  if (!headers.length) return;

  headers.forEach(header => {
    header.addEventListener('click', () => {
      const card = header.closest('.faq-card');
      const body = card.querySelector('.faq-body');
      const isOpen = card.classList.contains('active');

      // Close others
      document.querySelectorAll('.faq-card.active').forEach(openCard => {
        if (openCard !== card) {
          openCard.classList.remove('active');
          openCard.querySelector('.faq-header')?.setAttribute('aria-expanded', 'false');
          const openBody = openCard.querySelector('.faq-body');
          if (openBody) openBody.style.maxHeight = null;
        }
      });

      // Toggle current
      if (!isOpen) {
        card.classList.add('active');
        header.setAttribute('aria-expanded', 'true');
        body.style.maxHeight = `${body.scrollHeight}px`;
      } else {
        card.classList.remove('active');
        header.setAttribute('aria-expanded', 'false');
        body.style.maxHeight = null;
      }
    });
  });
}

/**
 * 7. Testimonials
 * Handles auto-scroll, drag, and touch interactions for testimonials.
 */
function initTestimonials() {
  const scrollers = document.querySelectorAll('.testimonials-scroller');
  if (!scrollers.length) return;

  scrollers.forEach((scroller) => {
    const cards = scroller.querySelectorAll('.testimonial-card');
    
    // Visibility fallback
    setTimeout(() => cards.forEach(c => c.classList.add('is-visible')), 500);

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        cards.forEach(card => card.classList.add('is-visible'));
        observer.disconnect();
      }
    }, { threshold: 0.1 });
    observer.observe(scroller);

    // Movement Engine
    let isDown = false;
    let startX;
    let scrollLeft;
    let autoTimer;

    const startAuto = () => {
      clearInterval(autoTimer);
      autoTimer = setInterval(() => {
        const max = scroller.scrollWidth - scroller.clientWidth;
        if (scroller.scrollLeft >= max - 10) {
          scroller.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          scroller.scrollBy({ left: scroller.clientWidth, behavior: 'smooth' });
        }
      }, 6000);
    };

    const stopAuto = () => clearInterval(autoTimer);
    startAuto();

    // Drag Events
    const stopDrag = () => {
      isDown = false;
      scroller.classList.remove('is-dragging');
      startAuto();
    };

    scroller.addEventListener('mousedown', (e) => {
      isDown = true;
      stopAuto();
      scroller.classList.add('is-dragging');
      startX = e.pageX - scroller.offsetLeft;
      scrollLeft = scroller.scrollLeft;
    });

    scroller.addEventListener('mouseleave', stopDrag);
    scroller.addEventListener('mouseup', stopDrag);
    
    scroller.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - scroller.offsetLeft;
      const walk = (x - startX) * 2;
      scroller.scrollLeft = scrollLeft - walk;
    });

    // Touch Events
    scroller.addEventListener('touchstart', stopAuto, { passive: true });
    scroller.addEventListener('touchend', startAuto, { passive: true });

    // Indicators
    const indicators = document.querySelectorAll('.scroll-indicator');
    const wrappers = document.querySelectorAll('.scroll-indicator-wrapper');

    scroller.addEventListener('scroll', () => {
      const max = scroller.scrollWidth - scroller.clientWidth;
      if (max <= 0) return;
      const prog = scroller.scrollLeft / max;
      
      if (indicators.length === 3) {
        indicators.forEach(i => i.classList.remove('active'));
        if (prog < 0.25) indicators[0].classList.add('active');
        else if (prog < 0.75) indicators[1].classList.add('active');
        else indicators[2].classList.add('active');
      }
    }, { passive: true });

    wrappers.forEach((wrap, idx) => {
      wrap.addEventListener('click', () => {
        stopAuto();
        const max = scroller.scrollWidth - scroller.clientWidth;
        let target = 0;
        if (idx === 1) target = max / 2;
        if (idx === 2) target = max;
        
        scroller.scrollTo({ left: target, behavior: 'smooth' });
        setTimeout(startAuto, 1000);
      });
    });
  });
}

/**
 * 8. Hero Animation
 * Uses Anime.js to animate hero elements on load.
 */
function initHeroAnimation() {
  const heroPanel = document.querySelector('.dlaz-hero__panel');
  if (!heroPanel) return;

  if (typeof anime !== 'undefined') {
    // Don't hide it. Ever.
    heroPanel.style.opacity = '1';

    // Slide in only (transform animation does not block paint)
    anime.set(heroPanel, { translateY: 30 });

    anime({
      targets: heroPanel,
      translateY: [30, 0],
      duration: 900,
      delay: 150,
      easing: 'easeOutExpo'
    });

    // Optional: animate only non-LCP fluff (buttons/bullets), NOT the H1
    const nonLcp = heroPanel.querySelectorAll(
      '.dlaz-hero__kicker, .dlaz-hero__lead, .dlaz-hero__bullets, .dlaz-hero__sep, .dlaz-hero__actions'
    );

    anime.set(nonLcp, { opacity: 0, translateY: 10 });

    anime({
      targets: nonLcp,
      opacity: [0, 1],
      translateY: [10, 0],
      duration: 500,
      delay: anime.stagger(80, { start: 250 }),
      easing: 'easeOutCubic'
    });
  }
}

/**
 * 9. Offcanvas Menu Animation
 * Layers an anime.js staggered reveal on top of Bootstrap's offcanvas.
 * Bootstrap keeps full control of open/close, toggler, focus trap, ESC and
 * backdrop — we only hook its lifecycle events to fade + slide the inner
 * blocks. Hidden state is set in JS on `show` only (never in CSS), so the
 * menu still works if JS or anime.js fails to load.
 */
function initOffcanvasAnimation() {
  const offcanvas = document.getElementById('navbarNavOffcanvas');
  if (!offcanvas || typeof anime === 'undefined') return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const items = () => offcanvas.querySelectorAll('.dlaz-oc-anim'); // DOM order

  const clearInline = () => {
    items().forEach((el) => {
      el.style.opacity = '';
      el.style.transform = '';
    });
  };

  // Open: hide the blocks immediately (no flash), then cascade them in from the
  // right as the panel arrives.
  offcanvas.addEventListener('show.bs.offcanvas', () => {
    const els = items();
    if (!els.length) return;
    if (reduceMotion) { clearInline(); return; }

    anime.set(els, { opacity: 0, translateX: 40 });
    anime({
      targets: els,
      opacity: [0, 1],
      translateX: [40, 0],
      delay: anime.stagger(60, { start: 120 }),
      easing: 'easeOutCubic',
      duration: 420
    });
  });

  // Close: quick fade + slide back out.
  offcanvas.addEventListener('hide.bs.offcanvas', () => {
    const els = items();
    if (!els.length || reduceMotion) return;
    anime({
      targets: els,
      opacity: [1, 0],
      translateX: [0, 30],
      easing: 'easeInQuad',
      duration: 200
    });
  });

  // Fully closed: reset inline styles so the next open starts clean.
  offcanvas.addEventListener('hidden.bs.offcanvas', clearInline);
}